import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';

import {catchError, combineLatest, map, Observable, of} from 'rxjs';

import {Measure, MeasureClass, MeasureClassOptions} from '@selpia/shared/models';

@Injectable({
  providedIn: 'root',
})
export class MeasureService {
  readonly #http: HttpClient = inject(HttpClient);
  readonly #url: string = S2WX_API_URL;

  getMeasures(): Observable<Measure[]> {
    return combineLatest([this.#loadMeasureList(), this.#loadMeasuresClasses()])
      .pipe(
        map(([measureList, measureClasses]) =>
          measureClasses?.length
            ? this.#addRelatedMeasures(measureList.map(measure => this.#enrichMeasureWithClassOptions(measure, measureClasses)))
            : measureList,
        ),
      );
  }

  #enrichMeasureWithClassOptions(measure: Measure, measureClasses: (MeasureClass & MeasureClassOptions)[]): Measure {
    const measureClass = measureClasses.find(c => c.name === measure.class);
    if (!measureClass) {
      return measure;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, prefer-const
    let {name, ...classOptions} = measureClass;
    try {
      classOptions = this.#fixClassOptions(classOptions);
    } catch (e) {
      console.error(`Failed to fix class options for measure ${measure.code}:`, e);
    }

    return ({
      ...measure,
      classOptions,
    });
  }

  #addRelatedMeasures(measures: Measure[]): Measure[] {
    return measures.map(measure =>
      ({
        ...measure,
        impactMeasure: measures.find(impactMeasure => impactMeasure.code === measure.impactMeasureCode),
        relativeGrowthMeasure: measures.find(relativeMeasure => relativeMeasure.code === measure.growthMeasureRelativeCode),
        absoluteGrowthMeasure: measures.find(absoluteMeasure => absoluteMeasure.code === measure.growthMeasureAbsoluteCode),
      }),
    );
  }

  #loadMeasureList(): Observable<Measure[]> {
    return this.#http.get<Measure[]>(`${this.#url}/Measures`);
  }

  #loadMeasuresClasses(): Observable<(MeasureClass & MeasureClassOptions)[]> {
    return this.#http.get<(MeasureClass & MeasureClassOptions)[]>(`${this.#url}/Measures/classes`)
      .pipe(
        catchError(() => {
          console.error('Failed to load measures classes options to format values! /Measures/classes');
          return of([]);
        }),
      );
  }

  #fixClassOptions(options: MeasureClassOptions): MeasureClassOptions {
    // @ts-expect-error: types do not match
    if (options.currencyDisplay === '') {
      delete options.currencyDisplay;
    }
    if (options.minimumFractionDigits > options.maximumFractionDigits) {
      options.maximumFractionDigits = options.minimumFractionDigits;
    }
    // @ts-expect-error: types do not match
    if (options.signDisplay === true) {
      options.signDisplay = 'always';
    }
    return options;
  }
}
